/**
 * Phase 5C — Tutor payout method card.
 * Appears at the top of the Tutor Portal Earnings tab.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Save, Wallet, AlertCircle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const TYPES = [
  { v: 'bank_transfer', label: 'Bank Transfer' },
  { v: 'mobile_money', label: 'Mobile Money' },
  { v: 'paypal', label: 'PayPal' },
  { v: 'wise', label: 'Wise' },
  { v: 'other', label: 'Other' },
];

const TutorPayoutMethodCard = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState(null);
  const [form, setForm] = useState({
    payout_method_type: 'bank_transfer',
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    branch_or_bsb: '',
    swift_bic: '',
    country: '',
    currency: 'AUD',
    mobile_money_provider: '',
    mobile_money_number: '',
    paypal_email: '',
    wise_email: '',
    notes: '',
  });

  const fetchMe = async () => {
    try {
      const r = await ax.get('/api/tutor/payout-method');
      setMethod(r.data?.method || null);
      if (r.data?.method) setForm((f) => ({ ...f, ...r.data.method }));
    } catch (_e) { /* tutor without method — that's fine */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchMe(); }, []);

  const save = async () => {
    if (!form.payout_method_type) return;
    setBusy(true);
    try {
      await ax.put('/api/tutor/payout-method', form);
      toast.success(method ? 'Payout method updated' : 'Payout method added');
      setEditing(false);
      await fetchMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="bg-white border border-gray-200 rounded-2xl p-5 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  }

  const missing = !method;
  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 mb-4" data-testid="tutor-payout-method-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-red-700" />
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Payout method</div>
            {missing ? (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-0.5">
                <AlertCircle className="h-2.5 w-2.5" /> Missing
              </span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 inline-flex items-center gap-0.5">
                <CheckCircle2 className="h-2.5 w-2.5" /> Active
              </span>
            )}
          </div>
          {missing ? (
            <p className="text-[12px] text-gray-600 mt-2">Add your payout method so the Winhouse team can pay you for completed lessons.</p>
          ) : (
            <p className="text-[12px] text-gray-600 mt-2">
              <strong>{(TYPES.find((t) => t.v === method.payout_method_type) || {}).label}</strong>
              {method.account_holder_name && <span> · {method.account_holder_name}</span>}
              {method.bank_name && <span> · {method.bank_name}</span>}
              {method.country && <span> · {method.country}</span>}
            </p>
          )}
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)}
            className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
            data-testid="payout-method-edit">
            <Plus className="h-3 w-3" /> {missing ? 'Add payout method' : 'Update'}
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Method type" testid="pm-type">
            <select value={form.payout_method_type}
              onChange={(e) => setForm({ ...form, payout_method_type: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Account holder name" testid="pm-holder">
            <Input v={form.account_holder_name} on={(v) => setForm({ ...form, account_holder_name: v })} />
          </Field>
          {form.payout_method_type === 'bank_transfer' && (
            <>
              <Field label="Bank name" testid="pm-bank"><Input v={form.bank_name} on={(v) => setForm({ ...form, bank_name: v })} /></Field>
              <Field label="Account number" testid="pm-acct"><Input v={form.account_number} on={(v) => setForm({ ...form, account_number: v })} /></Field>
              <Field label="Branch / BSB / Sort code" testid="pm-branch"><Input v={form.branch_or_bsb} on={(v) => setForm({ ...form, branch_or_bsb: v })} /></Field>
              <Field label="SWIFT / BIC (international)" testid="pm-swift"><Input v={form.swift_bic} on={(v) => setForm({ ...form, swift_bic: v })} /></Field>
            </>
          )}
          {form.payout_method_type === 'mobile_money' && (
            <>
              <Field label="Provider (e.g. M-Pesa)" testid="pm-mm-provider"><Input v={form.mobile_money_provider} on={(v) => setForm({ ...form, mobile_money_provider: v })} /></Field>
              <Field label="Mobile money number" testid="pm-mm-num"><Input v={form.mobile_money_number} on={(v) => setForm({ ...form, mobile_money_number: v })} /></Field>
            </>
          )}
          {form.payout_method_type === 'paypal' && (
            <Field label="PayPal email" testid="pm-paypal"><Input v={form.paypal_email} on={(v) => setForm({ ...form, paypal_email: v })} /></Field>
          )}
          {form.payout_method_type === 'wise' && (
            <Field label="Wise email" testid="pm-wise"><Input v={form.wise_email} on={(v) => setForm({ ...form, wise_email: v })} /></Field>
          )}
          <Field label="Country" testid="pm-country"><Input v={form.country} on={(v) => setForm({ ...form, country: v })} /></Field>
          <Field label="Currency" testid="pm-currency"><Input v={form.currency} on={(v) => setForm({ ...form, currency: v })} /></Field>
          <Field label="Notes (optional)" testid="pm-notes" full>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full h-20 px-3 py-2 rounded-lg border border-gray-200 text-sm" />
          </Field>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-[11px] font-bold uppercase tracking-wider" data-testid="payout-method-cancel">Cancel</button>
            <button onClick={save} disabled={busy} className="h-9 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="payout-method-save">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const Field = ({ label, children, testid, full }) => (
  <div className={full ? 'md:col-span-2' : ''} data-testid={testid}>
    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</label>
    {children}
  </div>
);
const Input = ({ v, on }) => (
  <input type="text" value={v || ''} onChange={(e) => on(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm" />
);

export default TutorPayoutMethodCard;
